import {
  CardStyleInterpolators,
  createStackNavigator,
} from "@react-navigation/stack";
import { Colors, Spacings } from "react-native-ui-lib";
import { useWithAppState } from "@towersystems/roam-common-native";
import { TextButton } from "@towersystems/roam-common-native";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  AuthorizedAppRootScreenParamList,
  SetupRootParamList,
} from "../../../../types";
import { SetupSelectLocationScreen } from "../SetupSelectLocationScreen";
import { SetupSelectTerminalScreen } from "../SetupSelectTerminalScreen";
import { SetupContext } from "./context";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useTranslation } from "../../../../utilities/i18n";
import React from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

const SetupStack = createStackNavigator<SetupRootParamList>();

export type SetupRootScreenProps = NativeStackScreenProps<
  AuthorizedAppRootScreenParamList,
  "SetupRootScreen"
>;

export const SetupRootScreen = ({ navigation }: SetupRootScreenProps) => {
  const [hasCheckedIfShouldRedirect, setHasCheckedIfShouldRedirect] =
    React.useState(false);

  const {
    appState: { locationId, terminalId },
  } = useWithAppState();

  const { currentVendor } = useCurrentVendorProvider();
  const { t } = useTranslation("navigation");
  const authentication = useAuthenticationProvider();
  const database = useDatabase();
  const { updateAppState } = useWithAppState();

  React.useEffect(() => {
    if (locationId && terminalId) {
      const id = setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: currentVendor.onboardingComplete
                ? "AppRootScreen"
                : "OnboardingRootScreen",
            },
          ],
        });
      }, 100);
      return () => clearTimeout(id);
    } else {
      setHasCheckedIfShouldRedirect(true);
    }
  }, [navigation, locationId, terminalId]);

  const resetAndLogoutFn = React.useCallback(() => {
    database
      .write(async () => {
        return database
          .unsafeResetDatabase()
          .catch((err) => console.log("FAILED TO RESET DB", err));
      })
      .then((i) => {
        updateAppState({ terminalId: null, locationId: null });
        authentication.logout();
      });
  }, [authentication]);

  if (!hasCheckedIfShouldRedirect) {
    return null; // or a loading spinner
  }

  return (
    <SetupContext.Provider
      value={{
        bootstrapped: true,
        setBootStrapped: () => {},
      }}
    >
      <SetupStack.Navigator
        screenOptions={{
          animationEnabled: true,
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          headerStyle: {
            backgroundColor: Colors.surface,
            borderBottomWidth: 1,
            borderBottomColor: Colors.borderColor,
          },
          cardStyle: { backgroundColor: Colors.surface, height: "100%" },
          title: "Setup",
          headerTitleAlign: "center",
          headerTitleStyle: {
            color: Colors.text,
          },
          headerLeftContainerStyle: { paddingLeft: Spacings.s5 },
          headerRightContainerStyle: { paddingRight: Spacings.s5 },
          headerLeft: () => (
            <TextButton onPress={resetAndLogoutFn}>
              {t("common:logout")}
            </TextButton>
          ),
        }}
      >
        <SetupStack.Screen
          name="SetupSelectLocationScreen"
          component={SetupSelectLocationScreen}
        />
        <SetupStack.Screen
          name="SetupSelectTerminalScreen"
          component={SetupSelectTerminalScreen}
        />
      </SetupStack.Navigator>
    </SetupContext.Provider>
  );
};
